/**
 * The capsule opening (REDESIGN.md §7).
 *
 * A capsule that has reached its date is not just another card: it is a letter
 * from somebody who no longer exists in quite that form. So opening it is a
 * full-screen moment — white light and turquoise particles — and the letter is
 * read there, alone, rather than discovered halfway down a list of rows.
 *
 * This is the one place in the app where something happens *to* you rather than
 * because you asked for it, so it is also the one place that takes the whole
 * screen. It closes to exactly where you were.
 *
 * Reduced motion skips the light entirely and shows the letter at once: the
 * ceremony is the reveal, not the particles.
 */

import { icon } from '../js/icons.js'
import { text } from '../js/html.js'

const PARTICLES = 70
const LIGHT_MS = 1700

/**
 * Opens the ceremony for a capsule.
 *
 * @param {{id:number, title?:string, body_html?:string, video_url?:string}} capsule
 * @param {{onOpened?: () => void}} handlers — called once the letter is shown,
 *        so the caller can record that it has been read
 * @returns {() => void} closes it early
 */
export function openCeremony(capsule, { onOpened = null } = {}) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const overlay = document.createElement('div')
  overlay.className = 'ceremony'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'Your capsule')
  overlay.innerHTML = `
    <canvas class="ceremony__light" width="760" height="760" aria-hidden="true"></canvas>

    <div class="ceremony__letter" data-letter hidden>
      <p class="ceremony__tag">Capsule · Open</p>
      <h1 class="ceremony__title">${text(capsule.title ?? 'A letter to yourself')}</h1>

      ${
        capsule.video_url
          ? `<video class="ceremony__video" src="${text(capsule.video_url)}" controls playsinline
                    preload="metadata"></video>`
          : ''
      }
      ${capsule.body_html ? `<div class="prose ceremony__prose">${capsule.body_html}</div>` : ''}

      <button class="btn" data-close>
        ${icon('check', { size: 17 })}<span>I have read it</span>
      </button>
    </div>
  `

  document.body.append(overlay)
  document.body.classList.add('is-ceremony')

  const canvas = overlay.querySelector('.ceremony__light')
  const letter = overlay.querySelector('[data-letter]')

  let frame = null
  let timer = null
  let closed = false

  function reveal() {
    letter.hidden = false
    overlay.classList.add('ceremony--open')
    overlay.querySelector('[data-close]')?.focus()
    onOpened?.()
  }

  function close() {
    if (closed) return
    closed = true

    window.cancelAnimationFrame(frame)
    window.clearTimeout(timer)
    document.body.classList.remove('is-ceremony')
    overlay.remove()
  }

  overlay.querySelector('[data-close]').addEventListener('click', close)
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close()
  })

  if (reduced) {
    canvas.remove()
    reveal()

    return close
  }

  // ---- the light ----------------------------------------------------------

  const context = canvas.getContext('2d')
  const centre = canvas.width / 2

  // Particles leave the centre at their own speed and angle, so the light
  // scatters rather than marching outwards in a ring.
  const dust = Array.from({ length: PARTICLES }, (_, index) => {
    const angle = (index / PARTICLES) * Math.PI * 2 + Math.random() * 0.4

    return {
      angle,
      speed: 90 + Math.random() * 190,
      size: 1 + Math.random() * 2.2,
      delay: Math.random() * 0.35,
    }
  })

  const started = performance.now()

  function paint(now) {
    const elapsed = (now - started) / LIGHT_MS

    context.clearRect(0, 0, canvas.width, canvas.height)

    // The core: a bloom that swells and then gives way to the letter.
    const bloom = Math.min(1, elapsed * 2.2)
    const fade = Math.max(0, 1 - Math.max(0, elapsed - 0.55) / 0.45)
    const glow = context.createRadialGradient(centre, centre, 0, centre, centre, 150 + bloom * 180)
    glow.addColorStop(0, `rgba(255, 255, 255, ${0.95 * fade})`)
    glow.addColorStop(0.45, `rgba(224, 247, 246, ${0.7 * fade})`)
    glow.addColorStop(1, 'rgba(224, 247, 246, 0)')
    context.fillStyle = glow
    context.fillRect(0, 0, canvas.width, canvas.height)

    for (const particle of dust) {
      const life = Math.max(0, elapsed - particle.delay)

      if (life <= 0) continue

      const distance = life * particle.speed * 2.4
      const alpha = Math.max(0, 1 - life * 1.15)

      context.fillStyle = `rgba(23, 190, 187, ${(alpha * 0.85).toFixed(3)})`
      context.beginPath()
      context.arc(
        centre + Math.cos(particle.angle) * distance,
        centre + Math.sin(particle.angle) * distance,
        particle.size * (1 - life * 0.4),
        0,
        7,
      )
      context.fill()
    }

    if (elapsed < 1.2) {
      frame = window.requestAnimationFrame(paint)
    }
  }

  frame = window.requestAnimationFrame(paint)
  timer = window.setTimeout(reveal, LIGHT_MS)

  return close
}
